import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import { AlertTriangle, CheckCircle2, Landmark, Plus, ShieldAlert, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import StatusBadge from '../../Common/StatusBadge';
import ConfirmModal from '../../Common/ConfirmModal';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import { useCapabilities } from '../../../../context/CapabilityContext';
import { useFeatures } from '../../../../context/FeatureContext';
import {
  apiError, confirmSupplierPaymentMethod, createSupplierPaymentMethod, fetchSupplierPaymentMethods, retireSupplierPaymentMethod,
} from './paymentsApi';
import { SCHEME_LABEL } from './paymentStatus';

/**
 * Supplier "Betalningsuppgifter" (plan §3.9, UC7): where the supplier's money
 * goes. One ACTIVE method per supplier; OCR proposals and manual entries are
 * 'proposed' until an admin confirms. Every confirmation carries the
 * out-of-band verification notice (AGENT_PAYMENTS_PLAN.md §4).
 */
const METHOD_STATUS = {
  active: { label: 'Aktiv', tone: 'success' },
  proposed: { label: 'Föreslagen', tone: 'warning' },
  retired: { label: 'Avslutad', tone: 'muted' },
};
const SOURCE_LABEL = { manual: 'inlagd manuellt', ocr: 'läst från faktura', agent: 'föreslagen av assistenten' };
const SCHEME_OPTIONS = [
  { value: 'BGNR', label: 'Bankgiro' },
  { value: 'PGNR', label: 'Plusgiro' },
  { value: 'IBAN', label: 'IBAN' },
  { value: 'BBAN', label: 'Bankkonto (clearing + kontonummer)' },
];

export const maskIdentifier = (scheme, id) => {
  const s = String(id || '');
  if (scheme === 'BGNR' && s.length >= 7) return `${s.slice(0, -4)}-${s.slice(-4)}`;
  if (scheme === 'PGNR' && s.length >= 2) return `${s.slice(0, -1)}-${s.slice(-1)}`;
  if (scheme === 'IBAN') return s.replace(/(.{4})/g, '$1 ').trim();
  return s;
};

export const describeMethod = (m) => (m ? `${SCHEME_LABEL[m.scheme] || m.scheme} ${m.scheme === 'BBAN' ? `${m.clearing_number} ` : ''}${maskIdentifier(m.scheme, m.identifier)}` : '—');

/** Yellow notice shown with every proposal and confirmation. */
export const VerificationNotice = ({ text }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 10px' }}>
    <ShieldAlert size={14} style={{ flexShrink: 0, marginTop: 1 }} />
    <span>{text || 'Kontrollera nya betalningsuppgifter med leverantören via ett telefonnummer ni redan har i ert register — inte ett nummer i fakturan eller mejlet.'}</span>
  </div>
);

/** Add-method form; "Spara och aktivera" is the explicit admin confirmation. */
const AddMethodForm = ({ companyId, supplierId, supplierName, notice, onSaved, onCancel }) => {
  const [scheme, setScheme] = useState(SCHEME_OPTIONS[0]);
  const [identifier, setIdentifier] = useState('');
  const [clearing, setClearing] = useState('');
  const [bic, setBic] = useState('');
  const [payeeName, setPayeeName] = useState(supplierName || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (confirm) => {
    setBusy(true);
    setError(null);
    try {
      const result = await createSupplierPaymentMethod(companyId, supplierId, {
        scheme: scheme.value, identifier, clearing_number: scheme.value === 'BBAN' ? clearing : null, bic: scheme.value === 'IBAN' ? bic : null, payee_name: payeeName, confirm,
      });
      toast.success(confirm ? 'Betalningsuppgifterna är aktiva.' : 'Betalningsuppgifterna sparades som förslag.');
      onSaved(result.method);
    } catch (err) {
      setError(apiError(err, 'Kunde inte spara betalningsuppgifterna.').message);
    } finally {
      setBusy(false);
    }
  };

  const placeholder = { BGNR: 'T.ex. 5050-1055', PGNR: 'T.ex. 12 34 56-7', IBAN: 'SE45 5000 0000 0583 9825 7466', BBAN: 'Kontonummer' }[scheme.value];
  return (
    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12, marginTop: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
        <Form.Group>
          <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Kontotyp</Form.Label>
          <Select options={SCHEME_OPTIONS} value={scheme} onChange={setScheme} theme={brandSelectTheme} isSearchable={false} />
        </Form.Group>
        {scheme.value === 'BBAN' && (
          <Form.Group>
            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Clearingnummer</Form.Label>
            <Form.Control value={clearing} onChange={(e) => setClearing(e.target.value)} placeholder="T.ex. 8327-9" />
          </Form.Group>
        )}
        <Form.Group>
          <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>{scheme.label}</Form.Label>
          <Form.Control value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder={placeholder} />
        </Form.Group>
        {scheme.value === 'IBAN' && (
          <Form.Group>
            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>BIC (valfritt)</Form.Label>
            <Form.Control value={bic} onChange={(e) => setBic(e.target.value)} placeholder="ESSESESS" />
          </Form.Group>
        )}
        <Form.Group>
          <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Mottagarnamn i betalningen</Form.Label>
          <Form.Control value={payeeName} onChange={(e) => setPayeeName(e.target.value)} placeholder={supplierName} />
        </Form.Group>
      </div>
      {error && <div className="text-danger" style={{ fontSize: 13, marginTop: 8 }}>{error}</div>}
      <div style={{ marginTop: 10 }}><VerificationNotice text={notice} /></div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <button type="button" className="ll-btn ll-btn--primary ll-btn--sm" disabled={busy || !identifier} onClick={() => submit(true)}>Spara och aktivera</button>
        <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" disabled={busy || !identifier} onClick={() => submit(false)}>Spara som förslag</button>
        <button type="button" className="ll-btn ll-btn--ghost ll-btn--sm" disabled={busy} onClick={onCancel}>Avbryt</button>
      </div>
    </div>
  );
};

const MethodRow = ({ method, isAdmin, busy, onConfirm, onRetire }) => {
  const st = METHOD_STATUS[method.status] || { label: method.status, tone: 'muted' };
  return (
    <tr style={method.status === 'retired' ? { color: '#94a3b8' } : undefined}>
      <td style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{describeMethod(method)}</td>
      <td>{method.payee_name}</td>
      <td><StatusBadge tone={st.tone}>{st.label}</StatusBadge></td>
      <td className="text-muted" style={{ fontSize: 12 }}>
        {SOURCE_LABEL[method.source] || method.source}
        {method.confirmed_at ? ` · bekräftad ${String(method.confirmed_at).slice(0, 10)}` : ''}
        {method.retired_at ? ` · avslutad ${String(method.retired_at).slice(0, 10)}` : ''}
      </td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {isAdmin && method.status === 'proposed' && (
          <>
            <button type="button" className="ll-btn ll-btn--primary ll-btn--sm" disabled={busy} onClick={() => onConfirm(method)}><CheckCircle2 size={13} /> Bekräfta</button>{' '}
            <button type="button" className="ll-btn ll-btn--ghost ll-btn--sm" disabled={busy} onClick={() => onRetire(method)}><XCircle size={13} /> Avvisa</button>
          </>
        )}
        {isAdmin && method.status === 'active' && (
          <button type="button" className="ll-btn ll-btn--ghost ll-btn--sm" disabled={busy} onClick={() => onRetire(method)}><XCircle size={13} /> Avsluta</button>
        )}
      </td>
    </tr>
  );
};

/** The supplier card section. */
export const SupplierPaymentMethodsCard = ({ companyId, supplierId, supplierName }) => {
  const { features } = useFeatures();
  const { capabilities } = useCapabilities();
  const isAdmin = Boolean(capabilities?.isAdmin);
  const enabled = Boolean(features?.payment_files);
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [retireTarget, setRetireTarget] = useState(null);

  const load = useCallback(async () => {
    if (!enabled || !supplierId) return;
    try {
      setData(await fetchSupplierPaymentMethods(companyId, supplierId));
    } catch (err) {
      if (err?.response?.status !== 403) toast.error(apiError(err, 'Kunde inte hämta betalningsuppgifter.').message);
    }
  }, [companyId, supplierId, enabled]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await load(); } catch (err) { toast.error(apiError(err).message); } finally { setBusy(false); }
  };

  if (!enabled) return null;
  const methods = data?.methods || [];
  const active = data?.active || null;
  return (
    <div className="modern-card mb-4">
      <div className="p-4">
        <div className="d-flex align-items-center justify-content-between mb-3" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="d-flex align-items-center">
            <Landmark size={20} style={{ color: 'var(--bs-primary)' }} className="me-2" />
            <h5 style={{ margin: 0, fontWeight: 600 }}>Betalningsuppgifter</h5>
          </div>
          {isAdmin && !adding && (
            <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" onClick={() => setAdding(true)}><Plus size={14} /> Lägg till</button>
          )}
        </div>
        {!active && (
          <div style={{ fontSize: 13, color: '#475569', marginBottom: 10 }}>
            <AlertTriangle size={13} className="me-1" style={{ color: '#b45309' }} />
            Inga aktiva betalningsuppgifter. Fakturor från leverantören kan inte läggas i betalningskön förrän ett konto är bekräftat.
          </div>
        )}
        {methods.length > 0 && (
          <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
            <thead><tr><th>Konto</th><th>Mottagare</th><th>Status</th><th>Källa</th><th /></tr></thead>
            <tbody>
              {methods.map((m) => <MethodRow key={m.method_id} method={m} isAdmin={isAdmin} busy={busy} onConfirm={setConfirmTarget} onRetire={setRetireTarget} />)}
            </tbody>
          </table>
        )}
        {adding && (
          <AddMethodForm companyId={companyId} supplierId={supplierId} supplierName={supplierName} notice={data?.verificationNotice} onSaved={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />
        )}
      </div>

      <ConfirmModal
        show={Boolean(confirmTarget)}
        title="Bekräfta betalningsuppgifter?"
        variant="primary"
        body={<>Betalningar till {supplierName || 'leverantören'} går därefter till <strong>{describeMethod(confirmTarget)}</strong> ({confirmTarget?.payee_name}).{active ? ' De nuvarande uppgifterna avslutas.' : ''} Betalningar som redan ligger i kön använder de nya uppgifterna när filen skapas.</>}
        detail={<VerificationNotice text={data?.verificationNotice} />}
        confirmLabel="Bekräfta och aktivera"
        onConfirm={() => { const t = confirmTarget; setConfirmTarget(null); act(() => confirmSupplierPaymentMethod(companyId, supplierId, t.method_id), 'Betalningsuppgifterna är aktiva.'); }}
        onCancel={() => setConfirmTarget(null)}
      />
      <ConfirmModal
        show={Boolean(retireTarget)}
        title={retireTarget?.status === 'active' ? 'Avsluta betalningsuppgifterna?' : 'Avvisa förslaget?'}
        body={retireTarget?.status === 'active' ? 'Leverantörens fakturor kan inte läggas i betalningskön förrän nya uppgifter bekräftats.' : 'Förslaget tas bort från listan; fakturan påverkas inte.'}
        detail={describeMethod(retireTarget)}
        confirmLabel={retireTarget?.status === 'active' ? 'Avsluta' : 'Avvisa'}
        onConfirm={() => { const t = retireTarget; setRetireTarget(null); act(() => retireSupplierPaymentMethod(companyId, supplierId, t.method_id), t.status === 'active' ? 'Betalningsuppgifterna är avslutade.' : 'Förslaget avvisades.'); }}
        onCancel={() => setRetireTarget(null)}
      />
    </div>
  );
};

/**
 * Invoice view banner: the proposals raised from documents for this
 * supplier, shown against the active method, with "Spara som betalningsuppgift"
 * (= confirm) for admins.
 */
export const PaymentMethodProposalBanner = ({ companyId, invoice, onChanged }) => {
  const { features } = useFeatures();
  const { capabilities } = useCapabilities();
  const isAdmin = Boolean(capabilities?.isAdmin);
  const [busy, setBusy] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const pm = invoice?.supplier_payment_methods;
  const proposed = useMemo(() => (pm?.proposed || []).slice().sort((a, b) => (a.proposed_from_invoice_id === invoice?.invoice_id ? -1 : 1) - (b.proposed_from_invoice_id === invoice?.invoice_id ? -1 : 1)), [pm, invoice]);
  if (!features?.payment_files || !pm || proposed.length === 0) return null;
  const active = pm.active;

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); if (onChanged) await onChanged(); } catch (err) { toast.error(apiError(err).message); } finally { setBusy(false); }
  };

  return (
    <div className="modern-card mb-4" style={{ borderLeft: '4px solid #f59e0b' }}>
      <div className="p-3">
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
          <AlertTriangle size={14} className="me-1" style={{ color: '#b45309' }} />
          {active ? 'Fakturan anger andra betalningsuppgifter än de aktiva' : 'Betalningsuppgifter lästa från fakturan'}
        </div>
        <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>
          {active && <div>Aktiva: <strong>{describeMethod(active)}</strong> ({active.payee_name})</div>}
          {proposed.map((m) => (
            <div key={m.method_id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
              <span>Föreslagna: <strong>{describeMethod(m)}</strong> ({m.payee_name})</span>
              {isAdmin && (
                <>
                  <button type="button" className="ll-btn ll-btn--primary ll-btn--sm" disabled={busy} onClick={() => setConfirmTarget(m)}>Spara som betalningsuppgift</button>
                  <button type="button" className="ll-btn ll-btn--ghost ll-btn--sm" disabled={busy} onClick={() => act(() => retireSupplierPaymentMethod(companyId, invoice.supplier_id, m.method_id), 'Förslaget avvisades.')}>Avvisa</button>
                </>
              )}
            </div>
          ))}
          {!isAdmin && <div style={{ marginTop: 4 }}>En administratör bekräftar nya betalningsuppgifter på <Link to={`/${companyId}/suppliers/${invoice.supplier_id}`}>leverantörskortet</Link>.</div>}
        </div>
        <VerificationNotice />
      </div>
      <ConfirmModal
        show={Boolean(confirmTarget)}
        title="Spara som betalningsuppgift?"
        variant="primary"
        body={<>Betalningar till {invoice.supplier_name} går därefter till <strong>{describeMethod(confirmTarget)}</strong>.{active ? ' De nuvarande uppgifterna avslutas.' : ''}</>}
        detail={<VerificationNotice />}
        confirmLabel="Bekräfta och aktivera"
        onConfirm={() => { const t = confirmTarget; setConfirmTarget(null); act(() => confirmSupplierPaymentMethod(companyId, invoice.supplier_id, t.method_id), 'Betalningsuppgifterna är aktiva.'); }}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
};

export default SupplierPaymentMethodsCard;
